import { ParsedUrlQuery } from 'querystring'

import { GetStaticProps } from 'next'
import { useState } from 'react'
import { Colors } from '@kvika/audur-theme'
import { asText } from '@prismicio/helpers'
import { PrismicRichText } from '@prismicio/react'

import { AudurButton } from '../../components/audurButtons'
import { GridItem, MainGridContainer } from '../../components/grid-container'
import CookieSettingsModal from '../../container/cookie-consent/CookieSettingsModal'
import Layout from '../../container/layout/Layout'
import AudurPrismicClient from '../../prismic/prismicClient'
import {
	AudurFooter,
	AudurHeader,
	AudurLegal,
	AudurLegalConnectionConnection,
	AudurLegalConnectionEdge,
	Maybe,
} from '../../prismic/prismicTypes'
import { prismicConstants, SITE_ALERT_REVALIDATE_SECONDS } from '../../utils/Constants'
import { Route } from '../../utils/navigation/Routes'
import { TranslatedStrings } from '../../utils/translations/TranslatedStrings'
import { ColumnClass, Gutters, MetaTagInfo, PreviewData, SiteAlert } from '../../utils/types/Types'
import { getAudurFooter, getAudurHeader, getAudurSiteAlert, getMetaTags } from '../../utils/Utils'

import styles from './uid.module.scss'

type Props = {
	maybeLegal: Maybe<AudurLegal>
	metaTags: MetaTagInfo
	footer: AudurFooter
	header: AudurHeader
	siteAlert: SiteAlert
}

const LegalTermsPage = ({ maybeLegal, metaTags, footer, header, siteAlert }: Props) => {
	const { cookieSettingsStrings } = TranslatedStrings.is
	const [showCookieSettingsModal, setShowCookieSettingsModal] = useState(false)

	return (
		<Layout title={asText(maybeLegal?.title)} metaTags={metaTags} footer={footer} header={header} siteAlert={siteAlert}>
			<MainGridContainer
				gutters={Gutters.Both}
				backgroundColor={Colors.Silver}
				style={{ paddingTop: 32, paddingBottom: 64 }}
			>
				<GridItem className={ColumnClass.All}>
					<div className={styles.container}>
						<PrismicRichText field={maybeLegal?.title} />
						<PrismicRichText field={maybeLegal?.content} />
						{maybeLegal?.showcustomcontent && Route.Cookies.endsWith(maybeLegal?._meta.uid ?? '') && (
							<div>
								<AudurButton
									onClick={() => setShowCookieSettingsModal(!showCookieSettingsModal)}
									style={{ backgroundColor: Colors.LightOrange, width: 300 }}
								>
									{cookieSettingsStrings.cookieSettings}
								</AudurButton>
							</div>
						)}
					</div>
				</GridItem>
			</MainGridContainer>
			<CookieSettingsModal visible={showCookieSettingsModal} onClose={() => setShowCookieSettingsModal(false)} />
		</Layout>
	)
}

export default LegalTermsPage

interface Params extends ParsedUrlQuery {
	uid: string
}

export const getStaticProps: GetStaticProps<Props, Params> = async ({ params, previewData }) => {
	const uId = params ? params.uid : ''
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const maybeLegal = await audurClient.getAudurLegalByUid({
		previewData: previewData as PreviewData,
		variables: { uId, lang: prismicConstants.apiDefaultLocale },
	})

	const [footer, header, siteAlert] = await Promise.all([getAudurFooter(), getAudurHeader(), getAudurSiteAlert()])

	const metaTags = getMetaTags({
		...maybeLegal,
		__typename: 'AudurLegal',
	} as Maybe<AudurLegal>)

	return {
		props: {
			maybeLegal,
			metaTags,
			footer,
			header,
			siteAlert,
		},
		revalidate: SITE_ALERT_REVALIDATE_SECONDS,
	}
}

export async function getStaticPaths() {
	const audurClient = new AudurPrismicClient(prismicConstants.apiToken)
	const maybeLegalConnection: Maybe<AudurLegalConnectionConnection> = await audurClient.getAllAudurLegals({
		variables: { lang: 'is' },
	})
	const edges = maybeLegalConnection ? maybeLegalConnection.edges : []
	const paths =
		edges?.map((maybeEdge: Maybe<AudurLegalConnectionEdge>) => {
			return maybeEdge
				? {
						params: {
							uid: `${maybeEdge.node._meta.uid}`,
							lang: `${maybeEdge.node._meta.lang}`,
						},
					}
				: { params: {} }
		}) || []

	return {
		paths,
		fallback: false,
	}
}
